import React, { useState } from "react";
import { Context } from "@/App";
import Card from "@/base-components/Card";
import Table, { TableProps } from "@/base-components/Table";
import { User, UserInstance } from "@/model/user";
import { Model } from "@/base-components/hooks/useModel";
import { addUserInstance, removeUserInstance, getAllInstances } from "@/api/user-controller";
import { Button } from "@/components/ui/button";
import Dropdown from "@/base-components/Dropdown";
import { DropdownItem } from "@/api/base/dropdown-item";
import { Box } from "@chakra-ui/react";
import { SPACING } from "@/generic/config";
import useToast from "@/base-components/hooks/useToast";

const UserInstances = ({ model }: { model: Model<User> }) => {
    const { translations } = React.useContext(Context);
    const [allInstances, setAllInstances] = useState<DropdownItem[]>([]);
    const [selectedInstance, setSelectedInstance] = useState<DropdownItem | null>(null);
    const [isAdding, setIsAdding] = useState(false);
    const toast = useToast();

    const loadInstances = async () => {
        const instances = await getAllInstances();
        const existingIds = model.object.userInstances.map((ui: UserInstance) => ui.instance.id);
        const available = instances
            .filter((inst: any) => !existingIds.includes(inst.id))
            .map((inst: any) => ({ id: inst.id, name: inst.identifier }));
        setAllInstances(available);
        setIsAdding(true);
    };

    const handleAdd = async () => {
        if (!selectedInstance) return;
        try {
            const newUserInstance = await addUserInstance(model.object.id, selectedInstance.id as number);
            model.setObject({
                ...model.object,
                userInstances: [...model.object.userInstances, newUserInstance],
            });
            setIsAdding(false);
            setSelectedInstance(null);
            toast("Added", "Instance added successfully", "success");
        } catch {
            toast("Error", "Failed to add instance", "error");
        }
    };

    const handleRemove = async (userInstance: UserInstance) => {
        try {
            await removeUserInstance(model.object.id, userInstance.instance.id);
            model.setObject({
                ...model.object,
                userInstances: model.object.userInstances.filter((ui: UserInstance) => ui.id !== userInstance.id),
            });
            toast("Removed", "Instance removed successfully", "success");
        } catch {
            toast("Error", "Failed to remove instance", "error");
        }
    };

    const tableProps: TableProps<UserInstance> = {
        columns: [
            { title: translations.model.user.id, scale: 1, getter: (r) => r.instance.id },
            { title: "Identifier", scale: 2, getter: (r) => r.instance.identifier },
            { title: "Environment", scale: 1, getter: (r) => r.instance.environment },
        ],
        rows: model.object.userInstances ?? [],
        contextMenuActions: [
            {
                title: translations.model.user.removeInstance,
                onClick: (row) => handleRemove(row),
            },
        ],
    };

    const addButton = model.isNew ? null : (
        <Button size="sm" onClick={loadInstances}>{translations.model.user.addInstance}</Button>
    );

    return (
        <Card {...translations.model.user.cards.instances} rightAddon={addButton}>
            <Table tableProps={tableProps} />
            {isAdding && (
                <Box display="flex" gap={SPACING} mt={SPACING} alignItems="center">
                    <Box flex={1}>
                        <Dropdown
                            items={allInstances}
                            value={selectedInstance}
                            placeholder="Select instance..."
                            onChange={(item) => setSelectedInstance(item)}
                        />
                    </Box>
                    <Button size="sm" onClick={handleAdd} disabled={!selectedInstance}>Add</Button>
                    <Button size="sm" variant="ghost" onClick={() => setIsAdding(false)}>Cancel</Button>
                </Box>
            )}
        </Card>
    );
};

export default UserInstances;
